import React, { useState } from 'react';
import { Form, Button, Card, Row, Alert } from 'react-bootstrap';
import { Link, Navigate } from 'react-router-dom';
import ApiHelper from '../../api/apiCallsHelper';
import config from '../../common/config';
import ToastService from '../Toasts/toastService';

const SignUp = () => {
  // Check if already logged in
  const isLoggedIn = localStorage.getItem('jwt') || sessionStorage.getItem('jwt');

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    passwordConfirmation: '',
    acceptedTerms: false,
    acceptedPrivacyPolicy: false
  });

  const [state, setState] = useState({
    errors: {},
    isLoading: false,
    redirectToLogin: false
  });

  const handleChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10,14}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  };

  const validateForm = () => {
    const errors = {};

    // Full Name validation
    if (!formData.fullName) {
      errors.fullName = 'Full name is required';
    } else if (formData.fullName.length < 3) {
      errors.fullName = 'Full name must be at least 3 characters';
    }

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Phone validation (optional, but if provided must be valid)
    if (formData.phone && !validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid phone number (10-14 digits)';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    // Password confirmation
    if (!formData.passwordConfirmation) {
      errors.passwordConfirmation = 'Please confirm your password';
    } else if (formData.password !== formData.passwordConfirmation) {
      errors.passwordConfirmation = 'Passwords do not match';
    }

    // Terms and conditions
    if (!formData.acceptedTerms) {
      errors.acceptedTerms = 'You must accept the Terms and Conditions';
    }

    // Privacy policy
    if (!formData.acceptedPrivacyPolicy) {
      errors.acceptedPrivacyPolicy = 'You must accept the Privacy Policy';
    }

    setState(prev => ({ ...prev, errors }));
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const user = {
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone
      };

      const result = await ApiHelper.post(
        `${config.trueFingerPrintsAPI.baseURL}/users/signUp`,
        user
      );

      if (result.status === 201) {
        ToastService.show(
          "Your account has been successfully created. You will have to verify your Email Address by clicking the link that you've received from us. Make sure to check your Spam folder as well.",
          'success'
        );

        setState(prev => ({
          ...prev,
          isLoading: false,
          redirectToLogin: true
        }));

        // Redirect to login after 3 seconds
        setTimeout(() => {
          setState(prev => ({ ...prev, redirectToLogin: true }));
        }, 3000);
      }
    } catch (error) {
      const errorMsg = error.data?.message || error.message || 'An error occurred. Please try again.';
      ToastService.show(errorMsg, 'error');
      setState(prev => ({
        ...prev,
        isLoading: false
      }));
    }
  };

  // Redirect if already logged in
  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  if (state.redirectToLogin) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div>
      <Row className="box-section banner">
        <div className='d-flex justify-content-center w-100 mb-2'>
          <div className="banner-text-group">
            <h2 className="headline mb-2 mt-0">Sign Up</h2>
            <h6 className="banner-text-item mb-2 mt-3">
              Creating an account with Personnelgraph is easy, just fill in the form below.
            </h6>
          </div>
        </div>
        <div className='d-flex justify-content-center w-100 mt-3'>
          <Card className="form-get-started p-4">
            <Card.Body className="p-0">
              <h3 className="mb-2 p-0 mt-3 text-center text-dark">Account Details</h3>
              <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-2">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Your Full Name"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      isInvalid={!!state.errors.fullName}
                      disabled={state.redirectToLogin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {state.errors.fullName}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Your E-mail Address"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      isInvalid={!!state.errors.email}
                      disabled={state.redirectToLogin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {state.errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      isInvalid={!!state.errors.password}
                      disabled={state.redirectToLogin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {state.errors.password}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Confirm Password"
                      name="passwordConfirmation"
                      value={formData.passwordConfirmation}
                      onChange={handleChange}
                      isInvalid={!!state.errors.passwordConfirmation}
                      disabled={state.redirectToLogin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {state.errors.passwordConfirmation}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Phone (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      isInvalid={!!state.errors.phone}
                      maxLength={14}
                      disabled={state.redirectToLogin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {state.errors.phone}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Check
                      type="checkbox"
                      className="pl-0"
                      name="acceptedTerms"
                      checked={formData.acceptedTerms}
                      onChange={handleChange}
                      isInvalid={!!state.errors.acceptedTerms}
                      disabled={state.redirectToLogin}
                      label={
                        <span>
                          I Accept{' '}
                          <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                            Terms and Conditions
                          </a>
                        </span>
                      }
                    />
                    {state.errors.acceptedTerms && (
                      <div className="text-danger small mt-1">
                        {state.errors.acceptedTerms}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check
                      type="checkbox"
                      className="pl-0"
                      name="acceptedPrivacyPolicy"
                      checked={formData.acceptedPrivacyPolicy}
                      onChange={handleChange}
                      isInvalid={!!state.errors.acceptedPrivacyPolicy}
                      disabled={state.redirectToLogin}
                      label={
                        <span>
                          I Accept{' '}
                          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                            Privacy Policy
                          </a>
                        </span>
                      }
                    />
                    {state.errors.acceptedPrivacyPolicy && (
                      <div className="text-danger small mt-1">
                        {state.errors.acceptedPrivacyPolicy}
                      </div>
                    )}
                  </Form.Group>
                  <Button
                    variant="primary btn-block"
                    type="submit"
                    disabled={state.isLoading || state.redirectToLogin}
                  >
                    {state.isLoading ? 'Creating Account...' : 'Sign Up'}
                  </Button>
                  <hr />
                  <div className="text-center mt-0 pt-0">
                    <h6 className="text-muted">Already have an account?</h6>
                    <Link to="/login">
                      <Button
                        variant="outline-primary d-flex justify-content-center mt-3"
                        className="w-100"
                      >
                        Login Here
                      </Button>
                    </Link>
                  </div>
                </Form>
            </Card.Body>
          </Card>
          </div>
      </Row>
    </div>
  );
};

export default SignUp;
